import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import Select from 'react-select';
import { Button, Col, Input, Nav, NavItem, NavLink, Row, Spinner, TabContent, TabPane, Table } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from '../hooks/index';
import { AFRDocumentStarter, BulkImportSender, DocumentReference, TaxonomyImportSender, bulkImportFinancialReportSpreadsheetFile, getTaxonomy, importTaxonomySpreadsheetFile } from '../redux/DocumentsSlice';
import { applyPatches } from '../redux/TaxonomyChangesSlice';
import { TaxonomyTypeEnum, TaxonomyVersionInfo, refreshTaxonomyCaches, selectAvailableVersions } from '../redux/TaxonomyVersionSlice';
import Page from './Page';
import { GenericActionTile } from './core/GenericActionTile';
import { GenericActionTileUploadFile } from './core/GenericActionTileUploadFile';
import TemplateMaintenance from './feature/TemplateMaintenance';



const TaxonomyManagement = () => {
    const dispatch = useAppDispatch();
    const afrTaxonomyVersions = useAppSelector(selectAvailableVersions);
    const [file, setFile] = React.useState<File | null>(null);
    const [afrYear, setAfrYear] = React.useState("0");
    const [isDownloadingTaxonomy, setIsDownloadingTaxonomy] = React.useState<boolean>(false);
    const [isDownloadingTaxonomyExcel, setIsDownloadingTaxonomyExcel] = React.useState<boolean>(false);
    const [isUploadingSpreadsheet, setIsUploadingSpreadsheet] = React.useState<boolean>(false);
    const [isUploadingAudits, setIsUploadingAudits] = React.useState<boolean>(false);
    const [isCreatingAfrTaxonomy, setIsCreatingAfrTaxonomy] = React.useState<boolean>(false);
    const [bulkPath, setBulkPath] = React.useState("");
    const [needsEntryPoint, setNeedsEntryPoint] = React.useState<boolean>(false);
    const [isRefreshingCache, setIsRefreshingCache] = React.useState<boolean>(false);
    const [isApplyingPatches, setIsApplyingPatches] = React.useState<boolean>(false);

    const [activeTab, setActiveTab] = React.useState('1');
    const toggle = (tab: any) => {
        if (activeTab !== tab) setActiveTab(tab);
    }


    const firstOption = { label: "Select Taxonomy", value: "0" };

    const getFiscalYears = function () {
        const d = new Date();
        let year = d.getFullYear();
        const maxYear = year + 1;
        var years = [{ value: "BASE", label: "BASE"}, { value: "2019", label: "AFR 2019" }];

        for (let i = 2020; i <= maxYear; i++) {
            years.push({ value: i.toString(), label: "AFR " + i.toString() });
        }
        years.push({ value: "COURT_REPORTING", label: "Court Reporting" });
        years.push({ value: "COURT_REPORTING_939", label: "Court Reporting 939.185" });
        years.push({ value: "COURT_REPORTING_29", label: "Court Reporting 29.0085" });
        years.unshift(firstOption);
        return years;
    }

    const ddFiscalYear = (< div >
        <Select
            value={
                getFiscalYears().filter(function (option) {
                    return option.value === afrYear;
                })
            }
            onChange={handleYearChange}
            options={getFiscalYears()}
        />
    </div >);

    function handleYearChange(event: any, action: any) {
        setAfrYear(event.value);
        if (event.value.indexOf("2") === 0) {
            setNeedsEntryPoint(true);
            // This is an afr year
            var selYear = parseInt(event.value);
            var vInfoList: TaxonomyVersionInfo[] = afrTaxonomyVersions.filter((x: TaxonomyVersionInfo) => x.year === selYear);
            if (vInfoList && vInfoList.length > 0) {
                var vInfo: TaxonomyVersionInfo = vInfoList[0];
                setNeedsEntryPoint( vInfo.isPublished === false );
            }
        } else {
            setNeedsEntryPoint(false);
        }
    }

    const handleRefreshClick = async function () {
        setIsRefreshingCache(true);
        await dispatch(refreshTaxonomyCaches("")).then((result: any) => {
            var vList: TaxonomyVersionInfo[] | null = result.payload;
            Swal.fire(
                'Refreshed!',
                'Taxonomy Cache Refreshed',
                'success'
            )
        });
        setIsRefreshingCache(false);
    }

    const handleApplyPatchesClick = async function () {
        setIsApplyingPatches(true);
        await dispatch(applyPatches("")).then((result: any) => {
            var pList: string[] = result.payload;
            Swal.fire(
                'Patches Applied!',
                JSON.stringify(pList),
                'success'
            )
        });
        setIsApplyingPatches(false);
    }

    const handleGenerate = async function () {
        var textResponse = 'The XBRL Taxonomy for ' + afrYear + ' is generated';
        if (afrYear > "0") {

            Swal.fire({
                title: 'Are you sure you want to generate an XBRL Taxonomy for ' + afrYear + '?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes'
            }).then((result: any) => {
                if (result.isConfirmed) {
                    generateAction("DOWNLOAD_TAXONOMY");
                    Swal.fire(
                        'Created!',
                        textResponse,
                        'success'
                    )
                }

            });

        }

    }
    function showDocumentResponse(response: any, textResponse : string ) {
        const doc = unwrapResult(response);
        if (doc != null) {
            if (doc.uri.indexOf("Error") < 0) {
                Swal.fire(textResponse, '', 'info');
            } else {
                Swal.fire(textResponse.replace("successful", "has errors - see Import Report"), '', 'error');
            }
            //TODO make this a generic function in document slice
            //and returns a blob.
            //TODO expand to handle xhtml and excel files
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            console.log("mime", doc.mimeType);
            fetch(url)
                .then(res => res.blob())
                .then(otho => {
                    const data = window.URL.createObjectURL(otho);
                    var link = document.createElement("a");
                    link.href = data;
                    link.download = doc.uri;
                    link.click();
                    setTimeout(function () {
                        // For Firefox it is necessary to delay revoking the ObjectURL
                        window.URL.revokeObjectURL(data);
                    }, 100);
                });
//                }
        } else {
            Swal.fire('Oops...', 'Something went wrong!', 'error')
        }

    }
    async function importTaxonomy(docType: string, uploadedFile: File) {

        if (uploadedFile !== null && docType !== null)
        {
            var tt: TaxonomyTypeEnum | null = null;
            var yr = parseInt(afrYear);
            switch (afrYear) {
                case "COURT_REPORTING":
                    tt = TaxonomyTypeEnum.COURT_REPORTING;
                    yr = 2011;
                    break;

                case "COURT_REPORTING_939":
                    tt = TaxonomyTypeEnum.COURT_REPORTING_939;
                    yr = 2011;
                    break;

                case "COURT_REPORTING_29":
                    tt = TaxonomyTypeEnum.COURT_REPORTING_29;
                    yr = 2011;
                    break;

                case "BASE":
                    tt = TaxonomyTypeEnum.BASE;
                    yr = 0;
                    break;

                default:
                    tt = TaxonomyTypeEnum.AFR;
                    break;
            }
            if (!tt) {
                alert("Invalid taxonomy choice: " + afrYear);
                return;
            }
            let fileSender: TaxonomyImportSender = {
                taxonomyVersion: {year: yr, type: tt, periods: null, isPublished: true }, file: uploadedFile, fileType: docType
            };
            var response = null;
            switch (docType) {
                case "UPLOAD_SPREADSHEET":
                    setIsUploadingSpreadsheet(true);
                    //if there are any section qualifiers - add them here, they only apply to spreadsheet uploads
                    response = await dispatch(importTaxonomySpreadsheetFile(fileSender));
                    setIsUploadingSpreadsheet(false);
                    break;

                case "UPLOAD_AUDITS":
                    setIsUploadingAudits(true);
                    //if there are any section qualifiers - add them here, they only apply to spreadsheet uploads
                    response = await dispatch(importTaxonomySpreadsheetFile(fileSender));
                    setIsUploadingAudits(false);
                    break;

                default:
                    alert("Invalid choice: " + docType);
                    return;
            }
            showDocumentResponse(response,'Upload successful');
        }
    }

    async function createAfrTaxonomy(docType: string, uploadedFile: File | null) {

        if (docType !== null)
        {
            var tt: TaxonomyTypeEnum = TaxonomyTypeEnum.AFR;
            var yr = parseInt(afrYear);
            let fileSender: TaxonomyImportSender = {
                taxonomyVersion: {year: yr, type: tt, periods: null, isPublished: false }, file: uploadedFile, fileType: docType
            };
            var response = null;
            switch (docType) {
                case "CREATE_AFR_TAXONOMY":
                    setIsCreatingAfrTaxonomy(true);
                    //if there are any section qualifiers - add them here, they only apply to spreadsheet uploads
                    response = await dispatch(importTaxonomySpreadsheetFile(fileSender));
                    setIsCreatingAfrTaxonomy(false);
                    break;

                default:
                    alert("Invalid choice: " + docType);
                    return;
            }
            showDocumentResponse(response,'Upload successful');
        }
    }

    const handleImport = function () {
        var textResponse = 'The Taxonomy for ' + afrYear + ' is imported';
        if (afrYear > "0") {

            Swal.fire({
                title: 'Are you sure you want to import an Taxonomy for ' + afrYear + '?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes'
            }).then((result: any) => {
                if (result.isConfirmed) {

                    //                    dispatch(ensureAfrsForYear(afrYear));

                    Swal.fire(
                        'Created!',
                        textResponse,
                        'success'
                    )
                }

            });

        }

    }

    async function downloadTaxonomyToBrowser(doc: DocumentReference) {
        if (doc != null) {
            var url = "data:" + doc.mimeType + ";base64," + doc.content;
            await fetch(url)
                .then(res => res.blob())
                .then(newBlob => {
                    const data = window.URL.createObjectURL(newBlob);
                    var link = document.createElement("a");
                    link.href = data;
                    link.download = doc.uri; //"TemplateDocument.xlsm";
                    link.click();
                    setTimeout(function () {
                        window.URL.revokeObjectURL(data);
                    })
                });
        }
    }

    async function downloadTaxonomy(docType: string, oType: string | null ) {
        var yr;
        var ecode;
        console.log(afrYear);
        switch (afrYear) {
            case "CourtReporting":
                yr = 2011;
                ecode = "CourtReporting";
                break;
            case "COURT_REPORTING":
                yr = 2011;
                ecode = afrYear;
                break;
            case "COURT_REPORTING_29":
                yr = 2011;
                ecode = afrYear;
                break;
            case "COURT_REPORTING_939":
                yr = 2011;
                ecode = afrYear;
                break;
            case "BASE":
                yr = 0;
                ecode = "BASE";
                break;
            default:
                yr = Number(afrYear);
                ecode = "AFR";
                break;
        }
        const request: AFRDocumentStarter = {
            afrYear: yr,
            entityCode: ecode,
            fileType: "TAXONOMY",
            outputType: oType,
            populate: 'NO'
        }
        const response = await dispatch(getTaxonomy(request));
        const doc = unwrapResult(response);
        if (doc !== null) {
            await downloadTaxonomyToBrowser(doc);
        }
    }

    const generateAction = async function (atype: string) {

        switch (atype) {
            case "DOWNLOAD_TAXONOMY":
                setIsDownloadingTaxonomy(true);
                await downloadTaxonomy(atype,null);
                setIsDownloadingTaxonomy(false);
                break;

        }
    }

    const importExportAction = async function (atype: string, ename: string, ecode: string, yr: number) {

        switch (atype) {
            case "CREATE_AFR_TAXONOMY":
                await createAfrTaxonomy(atype,null);
                break;

            case "DOWNLOAD_TAXONOMY":
                setIsDownloadingTaxonomy(true);
                await downloadTaxonomy(atype,null);
                setIsDownloadingTaxonomy(false);
                break;

            case "DOWNLOAD_TAXONOMY_DELTA":
                setIsDownloadingTaxonomyExcel(true);
                await downloadTaxonomy(atype,"Excel");
                setIsDownloadingTaxonomyExcel(false);
                break;
        }
    }

    const importExportActionUploadedFile = function (atype: string, ename: string, ecode: string, yr: number, uploadedFile: File) {
        setFile(uploadedFile);
        switch (atype) {
            case "UPLOAD_AUDITS":
            case "UPLOAD_SPREADSHEET":
            case "UPLOAD_INSTANCE":
                importTaxonomy(atype, uploadedFile);
                break;
        }

    }

    const handleBlur = function (event: React.FocusEvent<HTMLInputElement>) {


    }

    const IsInputTextValid = (textValue: string): boolean | undefined => {
        var isValid: boolean | undefined = undefined;
        if (textValue) {
            if (textValue.trim().length > 0) {
                isValid = true;
            }
            else {
                isValid = false;
            }
        }
        else {
            isValid = false;
        }
        return isValid
    }

    const IsInputTextInvalid = (textValue: string | undefined): boolean | undefined => {
        var isInvalid: boolean | undefined = undefined;

        if (textValue) {
            if (textValue.trim().length <= 0) {
                isInvalid = true;
            }
            else {
                isInvalid = false;
            }
        }
        else {
            isInvalid = true;
        }

        return isInvalid
    }

    const handleChange = function (event: React.FocusEvent<HTMLInputElement>) {

        var value = event.target.value;

        switch (event.target.name.toLowerCase()) {
            case "bulkpathinput":
                setBulkPath(value);
                break;
        }

    }

    async function bulkImport(tt: TaxonomyTypeEnum, tYear: number, baseYear: number, lp: string, dir : string, ftype : string) {
        var textResponse = 'The bulk updates for ' + tt + ' are imported';
        var eac = "*^" + baseYear + "^" + tt + "^" + lp;
        let sender: BulkImportSender = { reportRef: eac, afrYear: tYear, directory: dir, fileType: ftype };
        Swal.fire({
            title: 'Are you sure you want to import bulk updates for ' + tt + '?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes'
        }).then((result: any) => {
            if (result.isConfirmed) {

                setIsUploadingSpreadsheet(true);
                //if there are any section qualifiers - add them here, they only apply to spreadsheet uploads
                dispatch(bulkImportFinancialReportSpreadsheetFile(sender)).then((response : any) => 
                    showDocumentResponse(response, textResponse)
                );
                setIsUploadingSpreadsheet(false);

            }

        });


    }

    const handleBulkUpload29 = function () {
        bulkImport(TaxonomyTypeEnum.COURT_REPORTING_29, 2011, 0, "ANNUAL", bulkPath, "test/zip");
    }

    const handleBulkUpload939 = function () {
        bulkImport(TaxonomyTypeEnum.COURT_REPORTING_939, 2011, 0, "Q1", bulkPath, "test/zip");
    }
    const handleBulkUploadAFR = function () {
        bulkImport(TaxonomyTypeEnum.AFR, 2019, 2019, "ANNUAL", bulkPath, "application/json");
    }
    /*******************************************************************************/
    return (
        <Page>
            <Nav tabs>
                <NavItem>
                    <NavLink
                        active={activeTab === '1'}
                        onClick={() => { toggle('1'); }}>
                        Manage Taxonomies
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        active={activeTab === '2'}
                        onClick={() => { toggle('2'); }}>
                        Manage Templates
                    </NavLink>
                </NavItem>
            </Nav>
            <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                    <Row>
                        <h4 style={{ marginLeft: "1em" }}>Manage Taxonomies</h4>
                        <Table borderless >

                            <tbody>

                                <tr>
                                    <th className="col-2 text-right">Taxonomy:</th>
                                    <td className="col-4" colSpan={4}>{ddFiscalYear}</td>
                                </tr>
                                <tr>
                                    <td></td>
                                    <td className="col-2">
                                        <GenericActionTile
                                            actionType="CREATE_AFR_TAXONOMY"
                                            clickAction={importExportAction}
                                            entityName={"*"}
                                            entityCode={"*"}
                                            afrYear={2022}
                                            tileTitle="Create AFR Taxonomy"
                                            tileDescription="Create an AFR Taxonomy"
                                            icon="bi bi-diagram-3-fill"
                                            isEnabled={needsEntryPoint === true}
                                            isLoading={isCreatingAfrTaxonomy}
                                        />
                                    </td>
                                    <td className="col-2">
                                        <GenericActionTile
                                            actionType="DOWNLOAD_TAXONOMY_DELTA"
                                            clickAction={importExportAction}
                                            entityName={"*"}
                                            entityCode={"*"}
                                            afrYear={2022}
                                            tileTitle="Download Taxonomy Changes"
                                            tileDescription="Download as Spreadsheet"
                                            icon="bi bi-download"
                                            isEnabled={afrYear > "0"}
                                            isLoading={isDownloadingTaxonomyExcel}
                                        />
                                    </td>
                                    <td className="col-2">
                                        <GenericActionTile
                                            actionType="DOWNLOAD_TAXONOMY"
                                            clickAction={importExportAction}
                                            entityName={"*"}
                                            entityCode={"*"}
                                            afrYear={2022}
                                            tileTitle="Download XBRL Taxonomy"
                                            tileDescription="Download as XBRL"
                                            icon="bi bi-download"
                                            isEnabled={afrYear > "0"}
                                            isLoading={isDownloadingTaxonomy}
                                        />
                                    </td>
                                    <td>
                                        <GenericActionTileUploadFile
                                            actionType="UPLOAD_SPREADSHEET"
                                            clickAction={importExportActionUploadedFile}
                                            entityName={"*"}
                                            entityCode={"*"}
                                            afrYear={2022}
                                            tileTitle="Upload Taxonomy from Spreadsheet"
                                            tileDescription="Upload Taxonomy version from spreadsheet"
                                            icon="bi bi-arrow-bar-up"
                                            isEnabled={afrYear > "0"}
                                            uploadedFile={file}
                                            isLoading={isUploadingSpreadsheet}
                                        />
                                    </td>
                                    <td>
                                        <GenericActionTileUploadFile
                                            actionType="UPLOAD_AUDITS"
                                            clickAction={importExportActionUploadedFile}
                                            entityName={"*"}
                                            entityCode={"*"}
                                            afrYear={2022}
                                            tileTitle="Upload Audits from Zip"
                                            tileDescription="Upload Audits (Zip)"
                                            icon="bi bi-arrow-bar-up"
                                            isEnabled={afrYear > "0"}
                                            uploadedFile={file}
                                            isLoading={isUploadingAudits}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <th className="col-2 text-right">Cache Management:</th>
                                    <td>
                                        <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                            type="grow" color="warning"
                                            hidden={isRefreshingCache == false} />
                                        <div hidden={isRefreshingCache == true}>
                                            <Button type="button" onClick={handleRefreshClick}>Refresh Taxonomy Caches</Button>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <th className="col-2 text-right">Patch Management:</th>
                                    <td>
                                        <Spinner children={''} style={{ width: '1rem', height: '1rem' }}
                                            type="grow" color="warning"
                                            hidden={isApplyingPatches == false} />
                                        <div hidden={isApplyingPatches == true}>
                                            <Button type="button" onClick={handleApplyPatchesClick}>Apply Patches</Button>
                                        </div>

                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                        
                    </Row>
                </TabPane>
                <TabPane tabId="2" >
                    <Row className="w-100 ml-5">
                        <Col>
                            <TemplateMaintenance />
                        </Col>
                    </Row>
                </TabPane>
            </TabContent>
            
        </Page>
    );
}
export default TaxonomyManagement;
